import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Menu, Item, useContextMenu } from 'react-contexify';
import Button, { BUTTON_MODE, BUTTON_TYPE } from '@components/Button';
import styles from './styles.module.scss';
import FilterChip from '@components/FilterChip';
import FeatherIcon from 'feather-icons-react';
import Checkbox from '@components/Checkbox';
import { xor, noop } from 'lodash';
import { useWindowSize } from '@hooks/useWindowResize';

import Radio from '@components/Radio';
import ModalFilter from '@components/ModalFilter';
enum FACET_TYPE {
  CHECKBOX = 'checkbox',
  RADIO = 'radio',
}

interface IGenericCheckbox {
  value: string;
  label?: string;
  type: FACET_TYPE.CHECKBOX;
}

interface IGenericRadio {
  value: string;
  label?: string;
  type: FACET_TYPE.RADIO;
}

interface IProps {
  isActive: boolean;
  currentKey: string;
  setCurrentKey: (key: string) => void;
  id: string;
  label: string;
  icon?: string;
  disabled?: boolean;
  type: FACET_TYPE;
  selectedValues: string[];
  value: IGenericCheckbox[] | IGenericRadio[];
  onSetFilter: (id: string, filters: string[], type: FACET_TYPE) => void;
  onRemoveFilter: (id: string) => void;
  globalState: any;
}

export default function FilterDropdown({
  isActive,
  type,
  disabled,
  currentKey,
  setCurrentKey,
  id,
  icon,
  label,
  value,
  globalState,
  onSetFilter,
  selectedValues,
  onRemoveFilter,
}: IProps) {
  const { show, hideAll } = useContextMenu({ id });
  const MenuPosition = React.useRef({ x: 0, y: 0 });
  function onCloseMenu() {
    hideAll();
    setCurrentKey('');
  }
  const windowSize = useWindowSize();
  const isMobile = windowSize.width < 600;
  const { t } = useTranslation('library');

  const [state, setState] = React.useState<string[]>(selectedValues || []);
  const [dropdownIsOpen, setDropdownIsOpen] = React.useState(false);

  function handleOnHidden() {
    setCurrentKey('');
  }

  React.useEffect(() => {
    setState(selectedValues);
  }, [selectedValues]);

  function getMenuPosition(left: number, bottom: number) {
    MenuPosition.current = { x: left, y: bottom + 8 };
    return MenuPosition.current;
  }

  const displayModal = (id: string) => () => {
    setCurrentKey(id);
  };

  function dismissModal() {
    setCurrentKey('');
  }

  function displayDropdown(
    e: any,
    key: string,
    { left, bottom }: { left: number; bottom: number }
  ) {
    setDropdownIsOpen(true);
    window.addEventListener(
      'keydown',
      function (event) {
        event.stopPropagation();
      },
      true
    );
    setTimeout(() => {
      setCurrentKey(key);
    }, 10);
    show(e, {
      position: getMenuPosition(left, bottom),
    });
  }

  function handleItemClick({ event }: any) {
    // this will prevent the Menu from being closed
    event.stopPropagation();
  }

  const handleOnChangeSingleCheckbox = (value: string) => () => {
    setState(xor(state, [value]));
  };

  const handleOnChangeSingleRadio = (value: string) => () => {
    setState([value]);
  };

  function handleOnSetFilter() {
    onSetFilter(id, state, type);
  }

  function handleOnRemoveFilter() {
    onRemoveFilter(id);
  }

  function renderFacetCheckbox(value: any) {
    return (
      <Checkbox
        onChange={handleOnChangeSingleCheckbox(value.value)}
        checked={state.includes(value.value)}
        key={value.value}
      >
        <>{value?.label ? value.label : value.value} </>
      </Checkbox>
    );
  }

  function renderFacetRadio(value: any) {
    return (
      <Radio
        onChange={handleOnChangeSingleRadio(value.value)}
        checked={state.includes(value.value)}
        forLabel={value.value}
        key={value.value}
      >
        <>{value?.label ? value.label : value.value} </>
      </Radio>
    );
  }

  const renderValueInDropdown = (val: any, key: number) => {
    return (
      <React.Fragment key={key}>
        <Item aria-hidden onClick={handleItemClick}>
          {val.type === FACET_TYPE.CHECKBOX && renderFacetCheckbox(val)}
          {val.type === FACET_TYPE.RADIO && renderFacetRadio(val)}
        </Item>
      </React.Fragment>
    );
  };

  const renderValueInModal = (val: any, key: number) => {
    return (
      <React.Fragment key={key}>
        <div className={styles.checkboxWrapper}>
          {val.type === FACET_TYPE.CHECKBOX && renderFacetCheckbox(val)}
          {val.type === FACET_TYPE.RADIO && renderFacetRadio(val)}
        </div>
      </React.Fragment>
    );
  };

  function renderTitleModal(id: string) {
    switch (id) {
      case 'language':
        return t('filters.labels.language');
      case 'gender':
        return t('filters.labels.gender');
      case 'age':
        return t('filters.labels.age.voiceType');
      case 'accent':
        return t('filters.labels.accent');
      default:
        return '';
    }
  }

  return (
    <>
      <div className={styles.item}>
        <FilterChip
          isActive={isActive}
          isOnFocus={id === currentKey}
          id={id}
          label={label}
          disabled={disabled}
          range={globalState?.age ?? [0, 0]}
          onClick={isMobile ? displayModal(id) : displayDropdown}
          leadingIcon={
            <FeatherIcon
              strokeWidth="2px"
              icon={icon ?? ''}
              color={'var(--neutral600)'}
              size="18"
            />
          }
        />
      </div>

      {isMobile ? (
        <ModalFilter
          title={renderTitleModal(id)}
          isActive={isActive}
          isOpen={currentKey === id}
          onDismiss={dismissModal}
          onRemoveFilters={isActive ? handleOnRemoveFilter : onCloseMenu}
          onSetFilters={handleOnSetFilter}
        >
          {value.map(renderValueInModal)}
        </ModalFilter>
      ) : (
        <Menu
          id={id}
          animation={false}
          onHidden={dropdownIsOpen ? handleOnHidden : noop}
        >
          <div className={styles.dropdown}>
            <div className={styles.dropdownContent}>
              {value.length ? (
                value.map(renderValueInDropdown)
              ) : (
                <p> {t('filters.labels.noOptions')}</p>
              )}
            </div>
            <div className={styles.dropdownFooter}>
              <Button
                type={BUTTON_TYPE.PRIMARY}
                mode={BUTTON_MODE.BASIC}
                disabled={!value.length}
                onClick={handleOnSetFilter}
              >
                {t('filters.labels.apply')}
              </Button>
              <Button
                type={BUTTON_TYPE.PRIMARY}
                mode={BUTTON_MODE.GHOST}
                onClick={isActive ? handleOnRemoveFilter : onCloseMenu}
              >
                {isActive
                  ? t('filters.labels.remove')
                  : t('filters.labels.cancel')}
              </Button>
            </div>
          </div>
        </Menu>
      )}
    </>
  );
}
